import {
    type MoneyMapAccountWithAccountDetails,
    MoneyMapCategory,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { keyBy, orderBy } from 'lodash';
import { YStack } from 'smoothie';

import { getNumAccountsForCategory } from '../utils/moneyMapUtils';
import { type RuleVariant } from './MoneyMapAccountRule';
import {
    type MoneyMapAccountHolder,
    MoneyMapAccountRuleRedesign,
} from './MoneyMapAccountRuleRedesign';
import { MoneyMapCategoryHeaderRedesign } from './MoneyMapCategoryHeaderRedesign';
import { MoneyMapGoalExpandedRedesign } from './MoneyMapGoalExpandedRedesign';
import { MoneyMapGoalSummaryRedesign } from './MoneyMapGoalSummaryRedesign';

interface MoneyMapCategorySectionRedesignProps {
    category: MoneyMapCategory;
    moneyMap: MoneyMapWithRulesAndAccounts;
    accounts: MoneyMapAccountWithAccountDetails[];
    totalAmountCents: number | null;
    rules: MoneyMapRuleWithAccountDetails[];
    ruleVariant?: RuleVariant;
    accountHolders?: MoneyMapAccountHolder[];
    onPress?: () => void;
}

/**
 * Isolated copy of MoneyMapCategorySection for developing the next Money Map UI.
 * Keep the production component unchanged until the redesign is ready to replace it.
 */
export const MoneyMapCategorySectionRedesign = ({
    category,
    moneyMap,
    accounts,
    totalAmountCents,
    rules,
    ruleVariant,
    accountHolders,
    onPress,
}: MoneyMapCategorySectionRedesignProps) => {
    const accountsById = keyBy(accounts, 'id');

    const numAccounts = getNumAccountsForCategory(category, undefined, rules);
    const showGoalSummary =
        category === MoneyMapCategory.GOALS && ruleVariant === 'summary';
    const showGoalExpanded =
        category === MoneyMapCategory.GOALS && ruleVariant === 'full';

    return (
        <YStack
            marginBottom={8}
            borderRadius={24}
            overflow="hidden"
            backgroundColor="$gray100"
            onPress={onPress}
        >
            <MoneyMapCategoryHeaderRedesign
                category={category}
                numAccounts={numAccounts}
                totalAmountCents={totalAmountCents}
            />
            {showGoalSummary ? (
                <MoneyMapGoalSummaryRedesign
                    moneyMap={moneyMap}
                    rules={rules}
                />
            ) : showGoalExpanded ? (
                <MoneyMapGoalExpandedRedesign
                    moneyMap={moneyMap}
                    rules={rules}
                    accountsById={accountsById}
                    accountHolders={accountHolders}
                />
            ) : (
                <YStack>
                    {orderBy(rules, [
                        (rule) => {
                            if (rule.moneyMapRuleGroupId == null)
                                return rule.priority;
                            return (
                                moneyMap.moneyMapRuleGroups.find(
                                    (group) =>
                                        group.id === rule.moneyMapRuleGroupId,
                                )?.priority ?? rule.priority
                            );
                        },
                        'priority',
                    ]).map((rule, index) => {
                        return (
                            <MoneyMapAccountRuleRedesign
                                key={index}
                                category={category}
                                rule={rule}
                                moneyMap={moneyMap}
                                accountsById={accountsById}
                                showIncomeAccountBadge={
                                    rule.moneyMapAccountId ===
                                    moneyMap.incomeMoneyMapAccountId
                                }
                                variant={ruleVariant}
                                accountHolders={accountHolders}
                            />
                        );
                    })}
                </YStack>
            )}
        </YStack>
    );
};
